<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-primary-700/80 backdrop-blur px-4"
    @keydown.esc="save([])"
    @keydown.tab="trapFocus"
  >
    <div
      ref="dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="onboarding-title"
      aria-describedby="onboarding-subtitle"
      tabindex="-1"
      class="w-full max-w-xl rounded-2xl bg-primary-500 border border-primary-400 p-8"
    >
      <h2 id="onboarding-title" class="text-2xl font-semibold text-white">
        {{ $t("onboarding.title") }}
      </h2>
      <p id="onboarding-subtitle" class="text-subtle text-sm mt-2">
        {{ $t("onboarding.subtitle") }}
      </p>

      <!-- Genre checkboxes -->
      <div
        class="mt-6 flex flex-wrap gap-2"
        role="group"
        :aria-label="$t('onboarding.title')"
      >
        <button
          v-for="g in genres"
          :key="g.id"
          type="button"
          class="px-4 py-2.5 rounded-full text-sm transition border"
          :class="
            selected.includes(g.id)
              ? 'bg-highlight border-highlight text-primary-700 font-semibold'
              : 'bg-primary-700 border-primary-400 text-muted hover:text-highlight'
          "
          :aria-pressed="selected.includes(g.id) ? 'true' : 'false'"
          @click="toggle(g.id)"
        >
          {{ g.name }}
        </button>
      </div>

      <!-- Actions -->
      <div class="mt-8 flex items-center justify-end gap-4">
        <button
          type="button"
          class="px-4 py-3 text-muted text-sm hover:text-highlight uppercase tracking-widest"
          :disabled="saving"
          @click="save([])"
        >
          {{ $t("onboarding.skip") }}
        </button>
        <Button
          class="uppercase tracking-widest"
          :loading="saving"
          :disabled="selected.length === 0"
          @click.prevent="save(selected)"
        >
          {{ $t("onboarding.save") }}
        </Button>
      </div>
    </div>
  </div>
</template>

<script>
import Button from "@/components/common/Button.vue";

export default {
  name: "GenreOnboarding",
  components: { Button },

  data() {
    return {
      selected: [],
      saving: false,
    };
  },

  computed: {
    genres() {
      return this.$store.getters.genres;
    },
  },

  mounted() {
    if (!this.genres.length) {
      this.$store.dispatch("fetchGenres").catch(() => undefined);
    }
    // Move focus into the dialog so keyboard and screen reader users land
    // on it instead of continuing to tab through the page behind it.
    this.previouslyFocused = document.activeElement;
    this.$nextTick(() => this.$refs.dialog?.focus());
  },

  beforeDestroy() {
    // Return focus to wherever it was when the dialog opened.
    this.previouslyFocused?.focus?.();
  },

  methods: {
    /** Keep Tab inside the dialog while it is open. */
    trapFocus(event) {
      const focusable = this.$refs.dialog?.querySelectorAll(
        'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable || !focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (
        event.shiftKey &&
        (active === first || active === this.$refs.dialog)
      ) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    },
    toggle(id) {
      const i = this.selected.indexOf(id);
      if (i === -1) this.selected.push(id);
      else this.selected.splice(i, 1);
    },
    save(genres) {
      this.saving = true;
      this.$store
        .dispatch("saveUserGenres", genres)
        .then(() => {
          // Refresh recommendations so the new taste seeds take effect.
          this.$store.dispatch("fetchRecommendations").catch(() => undefined);
          this.$emit("done");
        })
        .catch(() => this.$emit("done"))
        .finally(() => (this.saving = false));
    },
  },
};
</script>
